import {
  InstanceMetrics,
  TableInstanceActionLogs
} from "modules/instances/types";
import { createSelector } from "reselect";
import { formatDate } from "utils/formatDate";
import { DATE_FORMATS } from "../../constants";
import { RootState } from "../../reducers";
import {
  Flavor,
  Image,
  Instance,
  InstanceFirewall,
  InstanceLimits,
  InstanceLog,
  SOURCE_TYPES,
  TableVirtualMachine
} from "./types";

export const SOURCE_TYPES_LABELS = {
  [SOURCE_TYPES.IMAGE]: "Image",
  [SOURCE_TYPES.VOLUME]: "Volume",
  [SOURCE_TYPES.SNAPSHOT]: "Snapshot"
};

export const instanceSelector = (state: RootState): Instance | null =>
  state.instances.instance;

export const instancesSelector = (state: RootState): Instance[] | null =>
  state.instances.instances;

// export const tableVirtualMachinesSelector = (
//   state: RootState
// ): TableVirtualMachine[] | null =>
//   state.instances.instances?.map((instance) => ({
//     id: instance.id,
//     name: instance.name,
//     status: instance.status,
//     ip: state.networks.interfaces
//       ? state.networks.interfaces
//           .filter((inter) => inter.device_id === instance.id)
//           .map((inter) => {
//             const network = state.networks.networks?.find(
//               (network) => network.id === inter.network_id
//             );
//             const floatingIPs = state.networks.floatingIPs;
//             if (network && inter && floatingIPs) {
//               if (inter.fixed_ips) {
//                 const associatedFloatingIP = floatingIPs.find(
//                   (fip) => fip.port_id === inter.id
//                 );
//                 if (associatedFloatingIP) {
//                   return `${network.name}:${
//                     inter.fixed_ips[0].ip_address || ""
//                   }, ${associatedFloatingIP.floating_ip_address} `;
//                 } else {
//                   return `${network.name}:${
//                     inter.fixed_ips[0].ip_address || ""
//                   } `;
//                 }
//               } else {
//                 return "";
//               }
//             }
//           })
//           .flat()
//           .join("\n")
//       : "",
//     keypair: instance.key_name,
//     vCPUs:
//       state.instances.flavors?.find(
//         (flavor) => flavor.id === instance.flavor_id
//       )?.vcpus || 0,
//     memory: state.instances.flavors
//       ? `${
//           (state.instances.flavors.find(
//             (flavor) => flavor.id === instance.flavor_id
//           )?.ram || 0) / 1024
//         } GiB`
//       : "",
//     flavorId: instance.flavor_id,
//     imageId: instance.image_id,
//     created: formatDate(new Date(instance.created), DATE_FORMATS.DATETIME),
//     tags: instance.tags,
//     tagsString: instance.tags.join(", ")
//   })) || null;

export const tableVirtualMachinesSelector = createSelector(
  [
    (state: RootState) => state.instances.instances,
    (state: RootState) => state.networks.interfaces,
    (state: RootState) => state.networks.networks,
    (state: RootState) => state.networks.floatingIPs,
    (state: RootState) => state.instances.flavors
  ],
  (
    instances,
    interfaces,
    networks,
    floatingIPs,
    flavors
  ): TableVirtualMachine[] | null =>
    instances?.map((instance) => {
      const ip = interfaces
        ? interfaces
            .filter((inter) => inter.device_id === instance.id)
            .map((inter) => {
              const network = networks?.find(
                (network) => network.id === inter.network_id
              );
              if (network && inter && floatingIPs) {
                if (inter.fixed_ips) {
                  const associatedFloatingIP = floatingIPs.find(
                    (fip) => fip.port_id === inter.id
                  );
                  if (associatedFloatingIP) {
                    return `${network.name}:${inter.fixed_ips
                      .map((fi) => fi.ip_address)
                      .filter(Boolean)
                      .join(
                        ", "
                      )} \n(fip: ${associatedFloatingIP.floating_ip_address})\n`;
                  } else {
                    return `${network.name}:${inter.fixed_ips
                      .map((fi) => fi.ip_address)
                      .filter(Boolean)
                      .join(", ")} `;
                  }
                }
              }
              return "";
            })
            .flat()
            .join("\n")
        : "";

      const flavor = flavors?.find(
        (flavor) => flavor.id === instance.flavor_id
      );

      return {
        id: instance.id,
        name: instance.name,
        status: instance.status,
        ip,
        keypair: instance.key_name,
        vCPUs: flavor?.vcpus || 0,
        memory: flavor ? `${Math.round((flavor.ram || 0) / 1024)} GiB` : "",
        flavorId: instance.flavor_id,
        flavorName: flavor?.name || "",
        imageId: instance.image_id,
        created: formatDate(new Date(instance.created), DATE_FORMATS.DATETIME),
        tags: instance.tags,
        tagsString: instance.tags.join(", "),
        fault_str: instance.fault.message
      };
    }) || null
);

export const isInstanceLoadingSelector = (state: RootState): boolean =>
  state.instances.isInstanceLoading;

export const isInstanceCreatingSelector = (state: RootState): boolean =>
  state.instances.isInstanceCreating;

export const isInstanceUpdatingSelector = (state: RootState): boolean =>
  state.instances.isInstanceUpdating;

export const isInstanceResizingSelector = (state: RootState): boolean =>
  state.instances.isInstanceResizing;

export const isInstanceResizeConfirmingSelector = (state: RootState): boolean =>
  state.instances.isInstanceResizeConfirming;

export const isInstanceResizeRevertingSelector = (state: RootState): boolean =>
  state.instances.isInstanceResizeReverting;

export const isInstanceDeletingSelector = (state: RootState): boolean =>
  state.instances.isInstanceDeleting;

export const isInstanceStartingSelector = (state: RootState): boolean =>
  state.instances.isInstanceStarting;

export const isInstanceRestartingSelector = (state: RootState): boolean =>
  state.instances.isInstanceRestarting;

export const isInstanceStoppingSelector = (state: RootState): boolean =>
  state.instances.isInstanceStopping;

export const isInstanceShelvingSelector = (state: RootState): boolean =>
  state.instances.isInstanceShelving;

export const areInstancesLoadingSelector = (state: RootState): boolean =>
  state.instances.areInstancesLoading;

export const flavorSelector = (state: RootState): Flavor | null =>
  state.instances.flavor;

export const flavorsSelector = (state: RootState): Flavor[] | null =>
  state.instances.flavors;

export const selectOptionFlavorsSelector = createSelector(
  [flavorsSelector],
  (flavors) => {
    // return flavors
    //   ? flavors.map((flavor) => ({
    //       id: flavor.id,
    //       name: `${flavor.name} (${flavor.vcpus} vCPU${
    //         flavor.vcpus > 1 ? "s" : ""
    //       }, ${Math.round(flavor.ram / 1024)} GiB memory)`
    //     }))
    //   : null;
    if (!flavors) {
      return null;
    }

    const vcFlavors = flavors
      .filter((flavor) => flavor.name.startsWith("VC"))
      .sort((a, b) => a.ram - b.ram || a.vcpus - b.vcpus);
    const otherFlavors = flavors.filter(
      (flavor) => !flavor.name.startsWith("VC")
    );

    return [...vcFlavors, ...otherFlavors].map((flavor) => ({
      id: flavor.id,
      name: `${flavor.name} (${flavor.vcpus} vCPU${
        flavor.vcpus > 1 ? "s" : ""
      }, ${Math.round(flavor.ram / 1024)} GiB memory)`
    }));
  }
);

// export const selectOptionFlavorsSelector = (
//   state: RootState
// ): SelectOptionFlavor[] | null =>
//   state.instances.flavors?.map((flavor) => ({
//     id: flavor.id,
//     name: `${flavor.name} (${flavor.vcpus} vCPU${
//       flavor.vcpus > 1 ? "s" : ""
//     }, ${flavor.ram / 1024} GiB memory)`
//   })) || null;

export const selectOptionGPUFlavorsSelector = createSelector(
  [flavorsSelector],
  (flavors) => {
    return flavors
      ? flavors
          .filter((flavor) => flavor.name.toLowerCase().includes("gpu"))
          .map((flavor) => ({
            id: flavor.id,
            name: `${flavor.name} (${flavor.gpu_count} ${flavor.gpu_type}, ${flavor.vcpus} vCPU${
              flavor.vcpus > 1 ? "s" : ""
            }, ${Math.round(flavor.ram / 1024)} GiB memory)`
          }))
      : null;
  }
);

export const selectOptionRegularFlavorsSelector = createSelector(
  [flavorsSelector],
  (flavors) => {
    return flavors
      ? flavors
          .filter((flavor) => !flavor.name.toLowerCase().includes("gpu"))
          .sort((a, b) => a.ram - b.ram || a.vcpus - b.vcpus)
          .map((flavor) => ({
            id: flavor.id,
            name: `${flavor.name} (${flavor.vcpus} vCPU${
              flavor.vcpus > 1 ? "s" : ""
            }, ${Math.round(flavor.ram / 1024)} GiB memory)`
          }))
      : null;
  }
);

export const isFlavorLoadingSelector = (state: RootState): boolean =>
  state.instances.isFlavorLoading;

export const areFlavorsLoadingSelector = (state: RootState): boolean =>
  state.instances.areFlavorsLoading;

// export const tableImagesSelector = (state: RootState): TableImage[] | null =>
//   state.instances.images?.map((image) => ({
//     id: image.id,
//     name: image.name,
//     status: image.status,
//     size: `${(image.size / 1024 / 1024 / 1024).toFixed(2)} GiB`,
//     createdAt: formatDate(new Date(image.created_at), DATE_FORMATS.DATETIME),
//     osPlatform: image.os_platform,
//     osDistro: image.os_distro,
//     osVersion: image.os_version,
//     osDefaultUser: image.os_default_user,
//     isDeprecated: image.deprecated
//   })) || null;

export const imagesSelector = (state: RootState): Image[] | null =>
  state.instances.images;

export const tableImagesSelector = createSelector(
  [imagesSelector],
  (images) => {
    return (
      images?.map((image) => ({
        id: image.id,
        name: image.name,
        status: image.status,
        size: `${(image.size / 1024 / 1024 / 1024).toFixed(2)} GiB`,
        createdAt: formatDate(
          new Date(image.created_at),
          DATE_FORMATS.DATETIME
        ),
        osPlatform: image.os_platform,
        osDistro: image.os_distro,
        osVersion: image.os_version,
        osDefaultUser: image.os_default_user,
        isDeprecated: image.deprecated
      })) || null
    );
  }
);

export const imageSelector = (state: RootState): Image | null =>
  state.instances.image;

export const areImagesLoadingSelector = (state: RootState): boolean =>
  state.instances.areImagesLoading;

// export const tableInterfacesSelector = (
//   state: RootState
// ): TableInterface[] | null =>
//   state.instances.interfaces?.map((i) => ({
//     ip: state.networks.floatingIPs?.find((fip) => fip.port_id === i.id)
//       ? `${i.fixed_ips ? i.fixed_ips[0].ip_address : ""}, ${
//           state.networks.floatingIPs.find((fip) => fip.port_id === i.id)
//             ?.floating_ip_address || ""
//         }`
//       : `${i.fixed_ips ? i.fixed_ips[0].ip_address : ""}`,
//     id: i.id,
//     subnetId: `${i.fixed_ips ? i.fixed_ips[0].subnet_id : ""}`,
//     name:
//       state.networks.subnets?.find((subnet) => {
//         if (i.fixed_ips) {
//           return subnet.id === i.fixed_ips[0].subnet_id;
//         }
//       })?.name || "",
//     macAddr: i.mac_address,
//     status: i.status,
//     firewalls: i.security_groups?.join(";\n") || "",
//     networkName: state.networks.networks?.find(
//       (network) => network.id === i.network_id
//     )?.name,
//     networkId: i.network_id
//   })) || null;

const getInterfaces = (state: RootState) => state.instances.interfaces;
const getFloatingIPs = (state: RootState) => state.networks.floatingIPs;
const getSubnets = (state: RootState) => state.networks.subnets;
const getNetworks = (state: RootState) => state.networks.networks;
export const tableVmInterfacesSelector = createSelector(
  [getInterfaces, getFloatingIPs, getSubnets, getNetworks],
  (interfaces, floatingIPs, subnets, networks) => {
    return (
      interfaces?.map((i) => ({
        // ip: floatingIPs?.find((fip) => fip.port_id === i.id)
        //   ? `${i.fixed_ips ? i.fixed_ips[0].ip_address : ""}, ${
        //       floatingIPs.find((fip) => fip.port_id === i.id)
        //         ?.floating_ip_address || ""
        //     }`
        //   : `${i.fixed_ips ? i.fixed_ips[0].ip_address : ""}`,
        ip: (() => {
          const fixed =
            i.fixed_ips?.map((ip) => ip.ip_address).filter(Boolean) || [];
          const fips =
            floatingIPs
              ?.filter((fip) => fip.port_id === i.id)
              .map((fip) => `fip:${fip.floating_ip_address}`)
              .filter(Boolean) || [];

          return fips.length > 0
            ? [...fixed, ...fips].join(", ")
            : fixed.join(", ");
        })(),
        id: i.id,
        // subnetId: `${i.fixed_ips ? i.fixed_ips[0].subnet_id : ""}`,
        subnetId:
          i.fixed_ips
            ?.map((ip) => ip.subnet_id)
            .filter(Boolean)
            .join(", ") || "",
        // name:
        //   subnets?.find(
        //     (subnet) => i.fixed_ips && subnet.id === i.fixed_ips[0].subnet_id
        //   )?.name || "",
        name:
          i.fixed_ips
            ?.map(
              (fi) =>
                subnets?.find((subnet) => subnet.id === fi.subnet_id)?.name
            )
            .filter(Boolean)
            .join(", ") || "",
        macAddr: i.mac_address,
        status: i.status,
        admin_state: i.admin_state_up,
        admin_state_str: i.admin_state_up ? "up" : "down",
        firewalls: i.security_groups?.join("; ") || "",
        networkName: networks?.find((network) => network.id === i.network_id)
          ?.name,
        networkId: i.network_id
      })) || null
    );
  }
);

export const selectOptionVmIPAddressesSelector = createSelector(
  [getInterfaces, getFloatingIPs],
  (interfaces, floatingIPs) =>
    interfaces?.flatMap((i) => [
      ...(i.fixed_ips
        ?.map((ip) => ip.ip_address)
        .filter(Boolean)
        .map((ip) => ({ label: ip, value: ip })) || []),
      ...(floatingIPs
        ?.filter((fip) => fip.port_id === i.id)
        .map((fip) => fip.floating_ip_address)
        .filter(Boolean)
        .map((ip) => ({ label: `${ip} (floating)`, value: ip })) || [])
    ])
);

export const areInterfacesLoadingSelector = (state: RootState): boolean =>
  state.instances.areInterfacesLoading;

export const isInterfaceCreatingSelector = (state: RootState): boolean =>
  state.instances.isInterfaceCreating;

export const isInterfaceDeletingSelector = (state: RootState): boolean =>
  state.instances.isInterfaceDeleting;

export const instanceLimitsSelector = (
  state: RootState
): InstanceLimits | null =>
  (state.instances.instanceLimits && state.instances.instanceLimits[0]) || null;

export const areInstanceLimitsLoadingSelector = (state: RootState): boolean =>
  state.instances.areInstanceLimitsLoading;

export const isVolumeAttachingSelector = (state: RootState): boolean =>
  state.instances.isVolumeAttaching;

export const isVolumeDetachingSelector = (state: RootState): boolean =>
  state.instances.isVolumeDetaching;

export const isFirewallAddingSelector = (state: RootState): boolean =>
  state.instances.isFirewallAdding;

export const isFirewallRemovingSelector = (state: RootState): boolean =>
  state.instances.isFirewallRemoving;

export const instanceFirewallsSelector = (
  state: RootState
): InstanceFirewall[] | null => state.instances.instanceFirewalls;

export const areInstanceFirewallsLoadingSelector = (
  state: RootState
): boolean => state.instances.areInstanceFirewallsLoading;

export const instanceLogSelector = (state: RootState): InstanceLog | null =>
  state.instances.instanceLog;

export const isInstanceLogLoadingSelector = (state: RootState): boolean =>
  state.instances.isInstanceLogLoading;

export const instanceMetricsSelector = (
  state: RootState
): InstanceMetrics | null => state.instances.instanceMetrics;

export const areInstanceMetricsLoadingSelector = (state: RootState): boolean =>
  state.instances.areInstanceMetricsLoading;

export const instanceActionLogsSelector = (
  state: RootState
): TableInstanceActionLogs[] | null =>
  state.instances.instanceActionLogs?.map((log) => ({
    ...log,
    id: log.request_id,
    start_time: formatDate(new Date(log.start_time), DATE_FORMATS.DATETIME)
  })) || null;

export const areInstanceActionLogsLoadingSelector = (
  state: RootState
): boolean => state.instances.areInstanceActionLogsLoading;
